/**
 * Reusable "Add Part" form used from both Settings → Catalog and the
 * top-level Parts List page (Jason 2026-07-08).
 *
 * Field rules match the CatalogItem contract:
 *   - SKU + Name           required
 *   - Category             optional
 *   - OEM #                optional
 *   - Unit price           optional (defaults to null in the API)
 *   - Lead time (days)     optional (defaults to 0 in the API)
 *   - Vendor               required for platform callers; auto-scoped
 *                          for vendor callers to their own company
 *
 * Wire-up: pass the vendor picklist + `onCreated(item)` and `onCancel()`
 * callbacks. The form owns its input state and error surface.
 */
import React, { useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import {
  type CatalogItem,
  type CreateItemBody,
  type VendorOption,
  createCatalogItem,
} from "../../services/catalogApi";

interface Props {
  vendors: VendorOption[];
  onCreated: (item: CatalogItem) => void;
  onCancel: () => void;
}

export const CreatePartForm: React.FC<Props> = ({
  vendors,
  onCreated,
  onCancel,
}) => {
  const { token, role } = useAuth();
  const isPlatform = role === "platform";
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [oem, setOem] = useState("");
  const [category, setCategory] = useState("");
  const [price, setPrice] = useState("");
  const [leadTime, setLeadTime] = useState("");
  const [vendorId, setVendorId] = useState<number | "">(
    isPlatform ? "" : (vendors[0]?.company_id ?? ""),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    setErr(null);
    try {
      const body: CreateItemBody = {
        sku: sku.trim(),
        name: name.trim(),
        oem_no: oem.trim() || null,
        category_name: category.trim() || null,
        unit_price: price ? Number(price) : null,
        lead_time_days: leadTime ? Number(leadTime) : 0,
      };
      if (isPlatform) {
        if (vendorId === "") {
          setErr("vendor is required");
          setBusy(false);
          return;
        }
        body.vendor_company_id = Number(vendorId);
      }
      const created = await createCatalogItem(token, body);
      onCreated(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white border border-secondary-200 rounded-md p-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs"
    >
      <label>
        <div className="text-secondary-600">SKU *</div>
        <input
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          required
          className="w-full border border-secondary-300 rounded px-2 py-1"
        />
      </label>
      <label className="md:col-span-2">
        <div className="text-secondary-600">Name *</div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="w-full border border-secondary-300 rounded px-2 py-1"
        />
      </label>
      <label>
        <div className="text-secondary-600">OEM #</div>
        <input
          value={oem}
          onChange={(e) => setOem(e.target.value)}
          className="w-full border border-secondary-300 rounded px-2 py-1"
        />
      </label>
      <label>
        <div className="text-secondary-600">Category</div>
        <input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full border border-secondary-300 rounded px-2 py-1"
        />
      </label>
      <label>
        <div className="text-secondary-600">Unit price</div>
        <input
          type="number"
          step="0.01"
          min={0}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="w-full border border-secondary-300 rounded px-2 py-1 font-mono"
        />
      </label>
      <label>
        <div className="text-secondary-600">Lead time (days)</div>
        <input
          type="number"
          step="1"
          min={0}
          max={3650}
          value={leadTime}
          onChange={(e) => setLeadTime(e.target.value)}
          className="w-full border border-secondary-300 rounded px-2 py-1 font-mono"
        />
      </label>
      {isPlatform && (
        <label className="md:col-span-2">
          <div className="text-secondary-600">Vendor *</div>
          <select
            value={vendorId === "" ? "" : String(vendorId)}
            onChange={(e) =>
              setVendorId(e.target.value === "" ? "" : Number(e.target.value))
            }
            required
            className="w-full border border-secondary-300 rounded px-2 py-1 bg-white"
          >
            <option value="">— Pick a vendor —</option>
            {vendors.map((v) => (
              <option key={v.company_id} value={v.company_id}>
                {v.company_name}
              </option>
            ))}
          </select>
        </label>
      )}
      {err && (
        <div className="md:col-span-3 text-error-700 bg-error-50 px-2 py-1 rounded">
          {err}
        </div>
      )}
      <div className="md:col-span-3 flex gap-2 justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={busy}>
          {busy ? "Saving…" : "Create part"}
        </Button>
      </div>
    </form>
  );
};

export default CreatePartForm;
